import { useEffect, useRef, type MouseEvent } from 'react';
import { useBrandIdentity } from '@/hooks/useBrandIdentity';
import { METHOD_LABELS } from '@/services/payments';
import { downloadReceiptPdf } from '@/lib/receiptPdf';

export interface ReceiptLine {
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface ReceiptData {
  title: string;
  number: string;
  date: string;
  customerName: string;
  customerPhone?: string;
  customerCity?: string;
  customerAddress?: string;
  salespersonName?: string;
  paymentMethod: string;
  paymentStatus: string;
  items: ReceiptLine[];
  total: number;
  notes?: string;
}

interface ReceiptModalProps {
  receipt: ReceiptData;
  onClose: () => void;
  // Cuando es true, se abre el diálogo de impresión del navegador apenas
  // se muestra la vista previa del comprobante (botón "Imprimir" del detalle).
  autoPrint?: boolean;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Comprobante / factura descargable. Se muestra la vista previa y el botón
// "Descargar" genera y baja un archivo PDF con el método nativo de jsPDF.
export default function ReceiptModal({ receipt, onClose, autoPrint = false }: ReceiptModalProps) {
  const identity = useBrandIdentity();
  const printedOnOpen = useRef(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Si se abrió con "Imprimir", disparamos el diálogo de impresión una sola vez
  // cuando la vista previa ya está en el DOM (así solo se imprime el comprobante).
  useEffect(() => {
    if (!autoPrint || printedOnOpen.current) return;
    printedOnOpen.current = true;
    const timer = setTimeout(() => window.print(), 250);
    return () => clearTimeout(timer);
  }, [autoPrint]);

  const paymentLabel = receipt.paymentMethod
    ? METHOD_LABELS[receipt.paymentMethod] || receipt.paymentMethod
    : '—';
  const statusLabel =
    receipt.paymentStatus === 'approved'
      ? 'Pagado'
      : receipt.paymentStatus === 'pending'
        ? 'Pendiente'
        : '—';

  // Descarga el comprobante como archivo PDF usando el método nativo de jsPDF
  // (.save()), que baja el archivo con su tipo MIME correcto (application/pdf).
  function handleDownload(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    downloadReceiptPdf({
      companyName: identity.companyName,
      companyPhone: identity.companyPhone,
      companyCity: identity.companyCity,
      companyLogo: identity.logo,
      title: receipt.title,
      number: receipt.number,
      date: receipt.date,
      customerName: receipt.customerName,
      customerPhone: receipt.customerPhone,
      customerCity: receipt.customerCity,
      customerAddress: receipt.customerAddress,
      salespersonName: receipt.salespersonName,
      paymentMethod: receipt.paymentMethod,
      paymentStatus: receipt.paymentStatus,
      items: receipt.items,
      total: receipt.total,
      notes: receipt.notes,
    });
  }

  // Imprime la vista del comprobante reutilizando el mismo cuerpo que se descarga.
  function handlePrint(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    window.print();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 print:hidden" onClick={onClose}></div>
      <div className="relative w-full max-w-xl max-h-[90vh] bg-white rounded-xl border border-background-200 overflow-hidden flex flex-col">
        {/* Barra superior (no se imprime) */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-100 print:hidden">
          <h2 className="text-base font-semibold text-foreground-950">{receipt.title}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 text-sm px-4 py-2 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-printer-line"></i>Imprimir
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-sm px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-2-line"></i>Descargar
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Cerrar"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Cuerpo del comprobante (área imprimible) */}
        <div className="overflow-y-auto px-5 py-5">
          <div id="receipt-print-area" className="text-foreground-900">
            {/* Encabezado con sello de marca + datos del remitente */}
            <div className="flex items-center gap-3 border-b-2 border-foreground-900 pb-3 mb-4">
              {identity.companyLogoUrl ? (
                <img
                  src={identity.companyLogoUrl}
                  alt={identity.companyName}
                  className="w-11 h-11 object-contain flex-shrink-0"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-pulse-line text-white text-lg"></i>
                </div>
              )}
              <div className="min-w-0">
                <h3 className="text-base font-bold text-foreground-950 truncate">{identity.companyName}</h3>
                {[identity.companyPhone, identity.companyCity].filter(Boolean).length > 0 && (
                  <p className="text-xs text-foreground-600">
                    {[identity.companyPhone, identity.companyCity].filter(Boolean).join(' · ')}
                  </p>
                )}
                <p className="text-xs text-foreground-500">www.paticasconproposito.com</p>
              </div>
              <div className="ml-auto text-right flex-shrink-0">
                <p className="text-[11px] uppercase tracking-wide text-foreground-500">
                  {receipt.title}
                </p>
                <p className="text-sm font-semibold text-foreground-950">#{receipt.number}</p>
              </div>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-foreground-500">Fecha</p>
                <p className="font-medium">{formatDate(receipt.date)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-foreground-500">Cliente</p>
                <p className="font-medium">{receipt.customerName || '—'}</p>
              </div>
              {receipt.customerPhone && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-foreground-500">Teléfono</p>
                  <p className="font-medium">{receipt.customerPhone}</p>
                </div>
              )}
              {receipt.customerCity && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-foreground-500">Ciudad</p>
                  <p className="font-medium">{receipt.customerCity}</p>
                </div>
              )}
              {receipt.customerAddress && (
                <div className="col-span-2">
                  <p className="text-[11px] uppercase tracking-wide text-foreground-500">Dirección</p>
                  <p className="font-medium">{receipt.customerAddress}</p>
                </div>
              )}
              {receipt.salespersonName && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-foreground-500">Vendedor</p>
                  <p className="font-medium">{receipt.salespersonName}</p>
                </div>
              )}
            </div>

            {/* Items */}
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="border-b border-foreground-300 text-left">
                  <th className="py-1.5 pr-2 text-[11px] uppercase tracking-wide text-foreground-500 font-medium">
                    Producto
                  </th>
                  <th className="py-1.5 pr-2 text-[11px] uppercase tracking-wide text-foreground-500 font-medium">
                    Talla
                  </th>
                  <th className="py-1.5 pr-2 text-[11px] uppercase tracking-wide text-foreground-500 font-medium text-right">
                    Cant.
                  </th>
                  <th className="py-1.5 pr-2 text-[11px] uppercase tracking-wide text-foreground-500 font-medium text-right">
                    P. Unit.
                  </th>
                  <th className="py-1.5 text-[11px] uppercase tracking-wide text-foreground-500 font-medium text-right">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody>
                {receipt.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-2 text-foreground-500">
                      Sin líneas registradas.
                    </td>
                  </tr>
                ) : (
                  receipt.items.map((it, idx) => (
                    <tr key={idx} className="border-b border-foreground-100">
                      <td className="py-1.5 pr-2">{it.productName || '—'}</td>
                      <td className="py-1.5 pr-2 text-foreground-700">{it.sizeLabel || '—'}</td>
                      <td className="py-1.5 pr-2 text-right">{it.quantity}</td>
                      <td className="py-1.5 pr-2 text-right">${it.unitPrice.toLocaleString()}</td>
                      <td className="py-1.5 text-right font-medium">${it.subtotal.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Totales y pago */}
            <div className="flex justify-between items-center border-t-2 border-foreground-900 pt-3 text-base font-bold">
              <span>Total</span>
              <span>${receipt.total.toLocaleString()}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-foreground-500">Medio de pago</p>
                <p className="font-medium">{paymentLabel}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-foreground-500">Estado</p>
                <p className="font-medium">{statusLabel}</p>
              </div>
            </div>

            {receipt.notes && (
              <div className="mt-3 text-xs text-foreground-600">
                <p className="font-medium mb-0.5">Notas</p>
                <p>{receipt.notes}</p>
              </div>
            )}

            {/* Pie */}
            <div className="mt-5 pt-3 border-t border-foreground-200 text-center text-xs text-foreground-500">
              <p className="font-semibold text-foreground-700">¡Gracias por tu compra!</p>
              <p className="mt-0.5">Cada paso apoya fundaciones de bienestar animal.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}